import {type Column, showNotification, Table} from 'platform/components';
import {Box, Text} from 'platform/foundation';

import {useState} from 'react';

import {
  useRemoveVehicleFromCampaignMutation,
  type VehicleCampaignResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useConfirmRepublishAds} from '../utils/confirmRepublishAds';
import {SalesCampaignRow} from './SalesCampaignRow';

interface SalesCampaignTableProps extends RequiredTestIdProps {
  campaigns: VehicleCampaignResponseBody[];
  vehicleId: string;
}

export function SalesCampaignTable(props: SalesCampaignTableProps) {
  const [removeVehicleFromCampaign] = useRemoveVehicleFromCampaignMutation();
  const confirmRepublishAds = useConfirmRepublishAds();
  const [pendingRemoveCampaignId, setPendingRemoveCampaignId] = useState<string | Nullish>(null);

  const handleRemove = (campaignId: string, republishAds?: boolean) =>
    removeVehicleFromCampaign({campaignId, vehicleId: props.vehicleId, republishAds})
      .unwrap()
      .then(() =>
        showNotification.success(
          republishAds ? i18n.t('entity.vehicle.labels.classifiedsWillBeUpdated') : undefined
        )
      )
      .catch(handleApiError);

  const handleRemoveVehicleFromCampaign = (campaign: VehicleCampaignResponseBody) => {
    if (!campaign.isApplied) {
      return handleRemove(campaign.id);
    }

    setPendingRemoveCampaignId(campaign.id);
    confirmRepublishAds(props.vehicleId, (republishAds) =>
      handleRemove(campaign.id, republishAds).then(() => setPendingRemoveCampaignId(null))
    );
  };

  const tableColumns: Column[] = [
    {
      element: (
        <Box paddingBottom={2}>
          <Text size="small" color="secondary" align="left">
            {i18n.t('entity.salesCampaign.labels.campaign')}
          </Text>
        </Box>
      ),
      width: '25%',
    },
    {
      element: (
        <Box paddingBottom={2}>
          <Text size="small" color="secondary" align="left">
            {i18n.t('entity.salesCampaign.labels.membershipType')}
          </Text>
        </Box>
      ),
      width: '20%',
    },
    {
      element: (
        <Box paddingBottom={2}>
          <Text size="small" color="secondary" align="left">
            {i18n.t('entity.salesCampaign.labels.validity')}
          </Text>
        </Box>
      ),
      width: '15%',
    },
    {
      element: (
        <Box paddingBottom={2}>
          <Text size="small" color="secondary" align="left">
            {i18n.t('entity.salesCampaign.labels.status')}
          </Text>
        </Box>
      ),
      width: '15%',
    },
    {
      element: (
        <Box paddingBottom={2}>
          <Text size="small" color="secondary" align="left">
            {i18n.t('entity.salesCampaign.labels.applied')}
          </Text>
        </Box>
      ),
      width: '15%',
    },
    {width: '10%'},
  ];

  return (
    <Table columns={tableColumns} tableLayout="fixed" data-testid={props['data-testid']}>
      {props.campaigns.map((campaign, index) => (
        <SalesCampaignRow
          key={campaign.id}
          campaign={campaign}
          onRemove={() => handleRemoveVehicleFromCampaign(campaign)}
          isRemoving={campaign.id === pendingRemoveCampaignId}
          data-testid={suffixTestId(`campaign-[${index}]`, props)}
        />
      ))}
    </Table>
  );
}
